import React from 'react';
import { getChildEntityBreakdown, getEntityTypeDisplay, pluralizeEntityType } from '../../../../../utils/entityDisplayUtils';

interface WorkItemBreakdownTooltipProps {
    childEntityTypes?: Record<string, number> | null;
    title?: string;
}

/**
 * A reusable tooltip content component that displays a breakdown of work item types.
 * Used in TopNavBar and EntityCountDisplay for consistent styling.
 */
export const WorkItemBreakdownTooltip: React.FC<WorkItemBreakdownTooltipProps> = ({
    childEntityTypes,
    title = "Work Item Breakdown:"
}) => {
    const breakdown = getChildEntityBreakdown(childEntityTypes);
    
    if (breakdown.length === 0) {
        return null;
    }
    return (
        <div className="p-3 -m-3 text-slate-700 dark:text-slate-200 flex flex-col items-start bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg min-w-[220px]">
            <div className="flex items-center gap-2 mb-1 pb-2 w-full border-b border-slate-200 dark:border-slate-600">
                <span className="text-sm font-semibold">{title}</span>
            </div>
            {breakdown.map((item) => {
                const { icon: ItemIcon, bgClass, iconClass } = getEntityTypeDisplay(item.type);
                return (
                    <div key={item.type} className="flex items-center justify-between w-full text-sm mt-1">
                        <div className="flex items-center gap-2.5">
                            <div className={`w-6 h-6 flex items-center justify-center rounded ${bgClass}`}>
                                <ItemIcon className={`w-3.5 h-3.5 ${iconClass}`} />
                            </div>
                            <span className="capitalize">{pluralizeEntityType(item.count, item.type)}</span>
                        </div>
                        <span className="font-semibold ml-4">{item.count}</span>
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Helper function to get the tooltip content for work item breakdown.
 * Returns the WorkItemBreakdownTooltip component or a fallback string.
 */
export const getWorkItemBreakdownTooltipContent = (
    childEntityTypes?: Record<string, number> | null,
    fallbackText: string = "Total work items count"
): React.ReactNode => {
    const breakdown = getChildEntityBreakdown(childEntityTypes);
    
    if (breakdown.length === 0) {
        return fallbackText;
    }

    return <WorkItemBreakdownTooltip childEntityTypes={childEntityTypes} />;
};

export default WorkItemBreakdownTooltip;
